/** A licence: given or sold to `email`, unlocking `unlocks`. `id` tells one from another. `U` is
 * the app's own list of what can be unlocked. */
export interface Licence<U extends string> {
  id: string;
  email: string;
  unlocks: U[];
}

/** What a code is checked against: the public keys trusted, and every unlock this app knows of. */
export interface VerifyOptions<U extends string> {
  publicKeys: readonly JsonWebKey[];
  unlocks: readonly U[];
}

const KEY = { name: 'ECDSA', namedCurve: 'P-256' } as const;
const SIGNATURE = { name: 'ECDSA', hash: 'SHA-256' } as const;

const toBase64Url = (bytes: Uint8Array) =>
  btoa(String.fromCharCode(...bytes)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');

const fromBase64Url = (text: string) =>
  Uint8Array.from(atob(text.replace(/-/g, '+').replace(/_/g, '/')), (c) => c.charCodeAt(0));

function parseLicence<U extends string>(json: string, known: readonly U[]): Licence<U> | null {
  try {
    const value: unknown = JSON.parse(json);
    if (typeof value !== 'object' || value === null) return null;
    const { id, email, unlocks } = value as Record<string, unknown>;
    if (typeof id !== 'string' || typeof email !== 'string' || !Array.isArray(unlocks)) return null;
    // One this version doesn't know of is left out, not a reason to refuse the rest.
    return { id, email, unlocks: known.filter((u) => unlocks.includes(u)) };
  } catch {
    return null;
  }
}

/**
 * The code for a licence: its JSON and its signature, each in base64url, with a dot between. Short
 * enough to paste from an email; signed on the developer's machine (an app's scripts/licence.ts) or,
 * later, by the payment's webhook, each with a private key of its own.
 */
export async function signLicence<U extends string>(licence: Licence<U>, privateKey: CryptoKey): Promise<string> {
  const payload = new TextEncoder().encode(JSON.stringify(licence));
  const signature = new Uint8Array(await crypto.subtle.sign(SIGNATURE, privateKey, payload));
  return `${toBase64Url(payload)}.${toBase64Url(signature)}`;
}

/**
 * The licence a code holds, if it was signed by one of `publicKeys`; null for anything else. Checked
 * on the device alone, so it works offline and nothing is sent anywhere. Spaces and line breaks an
 * email may have put in it are ignored.
 */
export async function verifyLicence<U extends string>(
  code: string,
  { publicKeys, unlocks }: VerifyOptions<U>,
): Promise<Licence<U> | null> {
  const [payloadText, signatureText, ...rest] = code.replace(/\s/g, '').split('.');
  if (!payloadText || !signatureText || rest.length > 0) return null;
  let payload: Uint8Array<ArrayBuffer>;
  let signature: Uint8Array<ArrayBuffer>;
  try {
    payload = fromBase64Url(payloadText);
    signature = fromBase64Url(signatureText);
  } catch {
    return null;
  }
  for (const jwk of publicKeys) {
    try {
      const key = await crypto.subtle.importKey('jwk', jwk, KEY, false, ['verify']);
      if (await crypto.subtle.verify(SIGNATURE, key, signature, payload)) {
        return parseLicence(new TextDecoder().decode(payload), unlocks);
      }
    } catch {
      // A key this browser can't read can't have signed it; the next may have.
    }
  }
  return null;
}
